import { Domain as AuthDomain } from 'auth-common/domain';
import type { Tagged } from 'type-fest';
import type { AggregationRoot } from '../../common';
import { SystemImmutableEntity as SIE } from '../../common';
import { UniqueId } from '../../common/id';
import { Identifier } from '../../common/identifier';
import type { MetadataField } from '../metadataField';
import { Session } from '../session';

import Project = AuthDomain.Object.Project;

import type { SystemCommandContext } from '../common';

export type Event = AggregationRoot &
  SIE & {
    id: Event.Id;
    projectId: Project.Id;
    eventType: Event.Type;
    sessionId: Session.Id;
    sessionIdentifiers: Session.Identifier[];
    identifiers: Identifier[];
    issuer: string;
    metadata: Event.Metadata | undefined;
  };

export namespace Event {
  /** イベント種別（例: 'smsOpened', 'voicebotCallStarted', 'webPageView'） */
  export type Type = Tagged<string, 'EventType'>;
  export namespace Type {
    /** 英数字および `_` のみ許可 */
    const Pattern = /^[a-zA-Z0-9_]+$/;
    export const FromString = (s: string): Type => {
      if (!Pattern.test(s)) {
        throw new Error(`Invalid Event.Type: ${JSON.stringify(s)} (allowed: [a-zA-Z0-9_]+)`);
      }
      return s as Type;
    };
  }

  // `sms_*` など Event.Type の拡張表現
  export type TypeExpression = Type | Tagged<string, 'EventTypeExpression'>;
  export namespace TypeExpression {
    export const FromString = (s: string): TypeExpression => {
      return s as TypeExpression;
    };
  }

  export type Id = Tagged<UniqueId, 'eventId'>;
  export namespace Id {
    export const Generate = () => UniqueId.generate() as Id;
    export function FromString(id: string): Id {
      return UniqueId.FromString(id) as Id;
    }
  }

  export type Metadata = Record<string, string | number | boolean>;

  export namespace Construct {
    export type Props = {
      id: string;
      projectId: string;
      eventType: string;
      sessionId: string;
      sessionIdentifiers: Session.Identifier.Props[];
      identifiers: Identifier.Props[];
      issuer: string;
      metadata: Metadata | undefined;
    } & SIE.Props;
  }

  export const Construct = (props: Construct.Props): Event => {
    return {
      id: Id.FromString(props.id),
      projectId: Project.Id.FromString(props.projectId),
      eventType: Type.FromString(props.eventType),
      sessionId: Session.Id.FromString(props.sessionId),
      sessionIdentifiers: props.sessionIdentifiers.map(Session.Identifier.Construct),
      identifiers: props.identifiers.map(Identifier.Construct),
      issuer: props.issuer,
      metadata: props.metadata,
      ...SIE.Construct(props),
    };
  };

  export namespace Create {
    export type Props = Omit<Construct.Props, 'id' | 'projectId' | 'createdAt'>;
    export type Params = {
      context: SystemCommandContext;
      props: Create.Props;
    };
  }
  export const Create = (params: Create.Params): Event => {
    const { context, props } = params;
    const { projectId, now } = context;

    return Construct({
      id: Id.Generate(),
      projectId,
      ...props,
      ...SIE.Create({ date: now }),
    });
  };

  export const IsEventFor = (event: Event, sessionId: Session.Id): boolean => {
    return event.sessionId === sessionId || event.sessionIdentifiers.some((id) => id.type === 'sessionId' && id.value === sessionId);
  };

  const castValue = (value: string | number | boolean, dataType: MetadataField.DataType): string | number | boolean => {
    switch (dataType) {
      case 'string':
        return String(value);
      case 'number': {
        const num = Number(value);
        return Number.isFinite(num) ? num : 0;
      }
      case 'boolean':
        return value === true || value === 'true' || value === 1 || value === '1';
    }
  };

  export const CastMetadata = (metadata: Metadata | undefined, metadataFields: MetadataField[]): Metadata | undefined => {
    if (!metadata) return undefined;
    const fieldMap = new Map(metadataFields.map((f) => [f.key as string, f.dataType]));
    return Object.fromEntries(
      Object.entries(metadata).map(([key, value]) => {
        const dataType = fieldMap.get(key);
        if (!dataType) return [key, value];
        return [key, castValue(value, dataType)];
      }),
    );
  };
}
